import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { procurementService } from '../../../../services/procurementService';
import type {
  Department,
  LegalEntity,
  ProcurementItemAdmin,
  UpsertProcurementItemRequest,
} from '../../../../types/procurement';
import { ItemSearchSelect } from '../../../common/ItemSearchSelect';
import { EditModal, FormField, inputClasses } from './EditModal';

export const ProcurementItemsSection: React.FC = () => {
  const [rows, setRows] = useState<ProcurementItemAdmin[]>([]);
  const [legalEntities, setLegalEntities] = useState<LegalEntity[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    mode: 'create' | 'edit';
    entity: UpsertProcurementItemRequest;
    originalKey?: { le: string; dept: string; item: string };
    itemDescription?: string | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, les, deps] = await Promise.all([
        procurementService.getAllProcurementItems(),
        procurementService.getLegalEntities(),
        procurementService.getAllDepartments(),
      ]);
      setRows(items);
      setLegalEntities(les);
      setDepartments(deps);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const departmentsForEditing = useMemo(() => {
    if (!editing) return [];
    return departments.filter((d) => d.legalEntityNo === editing.entity.legalEntityNo);
  }, [editing, departments]);

  const empty = (): UpsertProcurementItemRequest => ({
    legalEntityNo: legalEntities[0]?.legalEntityNo ?? '',
    departmentNo: '',
    procurementItemNo: '',
    procurementItemBarCode: '',
    recommendedQuantity: null,
    recommendedUnit: '',
  });

  const handleSave = async () => {
    if (!editing) return;
    const e = editing.entity;
    if (!e.legalEntityNo || !e.departmentNo || !e.procurementItemNo.trim()) {
      setError('Selskap, avdeling og varenummer er påkrevd');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.mode === 'create') {
        await procurementService.createProcurementItem(e);
      } else if (editing.originalKey) {
        await procurementService.updateProcurementItem(
          editing.originalKey.le, editing.originalKey.dept, editing.originalKey.item, e
        );
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ProcurementItemAdmin) => {
    if (!window.confirm(`Slette bestillingsvare ${row.procurementItemNo} fra ${row.legalEntityNo}/${row.departmentNo}?`)) return;
    try {
      await procurementService.deleteProcurementItem(row.legalEntityNo, row.departmentNo, row.procurementItemNo);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke slette');
    }
  };

  const field = <K extends keyof UpsertProcurementItemRequest>(key: K, value: UpsertProcurementItemRequest[K]) => {
    if (!editing) return;
    setEditing({ ...editing, entity: { ...editing.entity, [key]: value } });
  };

  const legalEntityName = (no: string) =>
    legalEntities.find((le) => le.legalEntityNo === no)?.legalEntityName ?? '';
  const departmentName = (le: string, dept: string) =>
    departments.find((d) => d.legalEntityNo === le && d.departmentNo === dept)?.departmentName ?? '';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Bestillingsvarer (ProcurementItems)</h2>
        <button
          onClick={() => setEditing({ mode: 'create', entity: empty() })}
          disabled={legalEntities.length === 0 || departments.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium px-3 py-1.5 rounded flex items-center gap-2 text-sm"
        >
          <Plus size={16} /> Ny bestillingsvare
        </button>
      </div>

      {error && !editing && (
        <div className="m-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={18} /> Laster…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">Ingen bestillingsvarer registrert.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left">Selskap</th>
              <th className="px-4 py-2 text-left">Avdeling</th>
              <th className="px-4 py-2 text-left">Varenr</th>
              <th className="px-4 py-2 text-left">Beskrivelse</th>
              <th className="px-4 py-2 text-left">GTIN</th>
              <th className="px-4 py-2 text-right">Anb. antall</th>
              <th className="px-4 py-2 text-left">Enhet</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-700">{r.legalEntityNo} – <span className="text-gray-500">{r.legalEntityName}</span></td>
                <td className="px-4 py-2 text-gray-700">{r.departmentNo} – <span className="text-gray-500">{r.departmentName}</span></td>
                <td className="px-4 py-2 font-medium">{r.procurementItemNo}</td>
                <td className="px-4 py-2 text-gray-700">{r.itemDescription || '—'}</td>
                <td className="px-4 py-2 text-gray-700 font-mono text-xs">{r.procurementItemBarCode || '—'}</td>
                <td className="px-4 py-2 text-right">{r.recommendedQuantity ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700">{r.recommendedUnit || '—'}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing({
                      mode: 'edit',
                      entity: {
                        legalEntityNo: r.legalEntityNo,
                        departmentNo: r.departmentNo,
                        procurementItemNo: r.procurementItemNo,
                        procurementItemBarCode: r.procurementItemBarCode ?? '',
                        recommendedQuantity: r.recommendedQuantity ?? null,
                        recommendedUnit: r.recommendedUnit ?? '',
                      },
                      originalKey: { le: r.legalEntityNo, dept: r.departmentNo, item: r.procurementItemNo },
                      itemDescription: r.itemDescription,
                    })}
                    className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 mr-1"
                    aria-label="Rediger"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(r)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                    aria-label="Slett"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditModal
          title={editing.mode === 'create' ? 'Ny bestillingsvare' : `Rediger ${editing.originalKey?.item}`}
          onClose={() => { setEditing(null); setError(null); }}
          onSave={handleSave}
          saving={saving}
          error={error}
          wide
          canSave={
            editing.entity.legalEntityNo.length > 0 &&
            editing.entity.departmentNo.length > 0 &&
            editing.entity.procurementItemNo.trim().length > 0
          }
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Selskap" required>
              <select
                value={editing.entity.legalEntityNo}
                disabled={editing.mode === 'edit'}
                onChange={(e) => {
                  if (!editing) return;
                  setEditing({
                    ...editing,
                    entity: { ...editing.entity, legalEntityNo: e.target.value, departmentNo: '' },
                  });
                }}
                className={`${inputClasses} ${editing.mode === 'edit' ? 'bg-gray-100' : ''}`}
              >
                {legalEntities.map((le) => (
                  <option key={le.legalEntityNo} value={le.legalEntityNo}>
                    {le.legalEntityNo} – {le.legalEntityName}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Selskapsnavn (read-only)">
              <input type="text" readOnly value={legalEntityName(editing.entity.legalEntityNo)}
                className={`${inputClasses} bg-gray-100`} />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Avdeling" required>
              <select
                value={editing.entity.departmentNo}
                disabled={editing.mode === 'edit'}
                onChange={(e) => field('departmentNo', e.target.value)}
                className={`${inputClasses} ${editing.mode === 'edit' ? 'bg-gray-100' : ''}`}
              >
                <option value="">-- Velg avdeling --</option>
                {departmentsForEditing.map((d) => (
                  <option key={d.departmentNo} value={d.departmentNo}>
                    {d.departmentNo} – {d.departmentName}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Avdelingsnavn (read-only)">
              <input type="text" readOnly
                value={departmentName(editing.entity.legalEntityNo, editing.entity.departmentNo)}
                className={`${inputClasses} bg-gray-100`} />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Varenummer (ItemNo)" required>
              {editing.mode === 'edit' ? (
                <input type="text" readOnly value={editing.entity.procurementItemNo}
                  className={`${inputClasses} bg-gray-100`} />
              ) : (
                <ItemSearchSelect
                  value={editing.entity.procurementItemNo}
                  selectedLabel={editing.itemDescription ?? undefined}
                  placeholder="Søk varenummer eller beskrivelse…"
                  onChange={(itemNo, item) => {
                    if (!editing) return;
                    setEditing({
                      ...editing,
                      entity: {
                        ...editing.entity,
                        procurementItemNo: itemNo,
                        recommendedUnit: editing.entity.recommendedUnit || item?.itemUnitCode || '',
                      },
                      itemDescription: item?.description1 ?? null,
                    });
                  }}
                />
              )}
            </FormField>
            <FormField label="Varenavn (read-only)">
              <input type="text" readOnly value={editing.itemDescription ?? ''}
                className={`${inputClasses} bg-gray-100`} />
            </FormField>
          </div>

          <FormField label="GTIN / strekkode">
            <input type="text" value={editing.entity.procurementItemBarCode ?? ''}
              onChange={(e) => field('procurementItemBarCode', e.target.value)}
              className={inputClasses} />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Anbefalt antall">
              <input type="number" step="any"
                value={editing.entity.recommendedQuantity ?? ''}
                onChange={(e) => field('recommendedQuantity', e.target.value === '' ? null : Number(e.target.value))}
                className={inputClasses} />
            </FormField>
            <FormField label="Anbefalt enhet">
              <input type="text" value={editing.entity.recommendedUnit ?? ''}
                onChange={(e) => field('recommendedUnit', e.target.value)}
                className={inputClasses} />
            </FormField>
          </div>
        </EditModal>
      )}
    </div>
  );
};
